import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { continueAsSavedAccount, RESUME_CLEARED_MESSAGE } from '../../lib/posResumeApi'
import { readPosResumeAccount, type PosResumeAccount } from '../../lib/posResumeLocal'
import AuthLayout, { PasswordField } from './AuthLayout'

export default function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [saved, setSaved] = useState<PosResumeAccount | null>(() => readPosResumeAccount())
  const [useOther, setUseOther] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handlePassword(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError('')
    if (!auth || auth.authError) {
      setError('Sign-in is temporarily unavailable. Please try again later.')
      return
    }
    setSubmitting(true)
    try {
      await auth.login(email.trim(), password)
      navigate('/app', { replace: true })
    } catch (err) {
      const code = (err as { code?: string }).code
      setError(
        code === 'auth/network-request-failed'
          ? 'Check your connection and try again.'
          : code === 'auth/too-many-requests'
            ? 'Too many attempts. Please wait a moment before trying again.'
            : 'We couldn’t sign you in. Check your email and password and try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleContinue() {
    if (submitting) return
    setError('')
    setSubmitting(true)
    try {
      await continueAsSavedAccount()
      navigate('/app', { replace: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      if (message === RESUME_CLEARED_MESSAGE) {
        setSaved(null)
        setUseOther(false)
      }
      setError(message || 'Waiting for a connection. We’ll sign you in when it’s back.')
    } finally {
      setSubmitting(false)
    }
  }

  const showContinue = Boolean(saved) && !useOther

  return (
    <AuthLayout>
      <p className="auth-eyebrow">YOUR WORKDAY STARTS HERE</p>
      <h1 className="auth-title">Welcome back.</h1>
      <p className="auth-subtitle">
        {showContinue ? 'Continue with the account already on this tablet.' : 'Sign in to your Wortelogy account.'}
      </p>
      {showContinue && saved ? (
        <div className="auth-form">
          {error && (
            <div className="auth-error" role="alert">
              {error}
            </div>
          )}
          <div className="auth-invitation">
            <h2>{saved.name}</h2>
            <p>{saved.email}</p>
          </div>
          <button className="auth-btn auth-btn--primary" type="button" disabled={submitting} onClick={() => void handleContinue()}>
            {submitting ? 'Signing in…' : <>Continue as {saved.name} <span aria-hidden="true">→</span></>}
          </button>
          <button
            type="button"
            className="auth-forgot"
            onClick={() => {
              setError('')
              setUseOther(true)
            }}
          >
            Use a different account
          </button>
        </div>
      ) : (
        <form onSubmit={(e) => void handlePassword(e)} className="auth-form">
          {error && (
            <div className="auth-error" role="alert">
              {error}
            </div>
          )}
          <label className="auth-label">
            Email address
            <input
              className="auth-input"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@yourbusiness.com"
            />
          </label>
          <PasswordField value={password} onChange={setPassword} />
          <Link to="/forgot-password" className="auth-forgot">
            Forgot password?
          </Link>
          <button className="auth-btn auth-btn--primary" disabled={submitting}>
            {submitting ? 'Signing in…' : <>Sign in <span aria-hidden="true">→</span></>}
          </button>
          {saved && (
            <button
              type="button"
              className="auth-forgot"
              onClick={() => {
                setError('')
                setUseOther(false)
              }}
            >
              Back to {saved.name}
            </button>
          )}
        </form>
      )}
      <p className="auth-footer">
        New to Wortelogy? <Link to="/register">Get started</Link>
      </p>
      <p className="auth-hint">Joining an existing team? Sign in with the account your owner or manager created for you.</p>
    </AuthLayout>
  )
}
